export default {
  id: 'reading-report',
  title: 'Reading Your Report',
  content: `## Reading Your Trade Report

Every Trade Report follows the same structure. Here is what each section contains and how to use it.

---

## Section 1 — Executive Summary

A one-page overview at the top of every report:

| Field | Description |
|---|---|
| Period | Date range covered |
| Total trades | Number of completed trades |
| Net P&L | Total profit or loss for the period |
| Win rate | % of trades that closed profitable |
| Profit factor | Gross wins ÷ gross losses |
| Discipline Score | Your score at end of period |
| Behavioral Profile | Your profile label at end of period |

Use this section for a quick health check. If P&L is positive but discipline score is low, you got lucky — your process has problems. If P&L is negative but discipline score is high, your process is sound — variance is the issue, not behaviour.

---

## Section 2 — P&L Analysis

Detailed breakdown of your profit and loss:

- **Cumulative P&L chart** — your running account value over the period
- **Daily P&L bar chart** — which days were profitable, which were not
- **P&L by symbol** — which stocks contributed most to gains and losses
- **P&L by trade type** — INTRADAY vs DELIVERY split
- **Avg win vs avg loss** — the ratio that determines long-term viability
- **Largest single win and largest single loss** — outliers that deserve investigation

---

## Section 3 — Trade Quality Metrics

| Metric | What it measures |
|---|---|
| Plan completeness % | What % of trades had a full plan (entry + stop + target) |
| R:R achieved vs planned | Did your actual exits match your planned R:R? |
| Stop adherence rate | What % of the time did you exit at your stop (vs holding through it)? |
| Early exit rate | What % of winning trades did you exit before target? |
| Avg hold time (wins) | How long you held winning trades |
| Avg hold time (losses) | How long you held losing trades |

> If avg hold time on losses > avg hold time on wins, you are cutting winners short and letting losers run. This is the most common way disciplined traders underperform their own strategy.

---

## Section 4 — Behavioral Flags

A list of every behavioral flag detected during the period:

| Flag | Count | What to do |
|---|---|---|
| Revenge trades | N | Review the trades immediately after losses — were they lower quality? |
| Stop avoidance | N | For each instance: how far past your stop did you hold? |
| FOMO entries | N | Were your entry prices far above the signal price? |
| Oversize positions | N | Which trades exceeded your Risk Per Trade %? |
| Overtrading days | N | On which days did trade count spike? What was the P&L those days? |

A flag count of 0 for the period means clean discipline across all dimensions.

---

## Section 5 — ACT Performance Summary

Your ACT session results for the period:

| Metric | Description |
|---|---|
| Sessions completed | Total ACT sessions in the period |
| Avg overall score | Your average score across all sessions |
| Avg Setup Quality | Average score on setup reading |
| Avg Entry Quality | Average score on entry timing |
| Avg Risk Management | Average score on stop/target/R:R |
| Avg Decision Quality | Average score on Enter/Skip correctness |
| Most common Skip tag | The reason you most often skipped setups |
| Conviction accuracy | Win rate on HIGH conviction vs LOW conviction entries |

---

## Section 6 — Recommendations

The final section of the report contains 3–5 specific, data-driven recommendations generated from your metrics. These are not generic tips — they are derived from your actual numbers.

**Examples:**
> *Your avg loss (₹8,200) is 2.1× your avg win (₹3,900). Focus on holding winners longer — review the 14 trades this period where you exited before target.*

> *Stop avoidance occurred 6 times this period. In all 6 cases, the trade continued past the stop and closed at a larger loss. Your stops are well-placed — the problem is execution.*

> *Your ACT Risk Management score averaged 61 this period. 9 of your 28 plans had R:R below 2.0. Set a rule: if R:R < 2.0, the plan does not get submitted.*`,
};
